import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import './WaitPatternHeatmapManager.css';

function formatWhen(value) {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleString();
}

export default function WaitPatternHeatmapManager() {
  const toast = useRef(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);

  const loadStatus = useCallback(async () => {
    const { data } = await api.get('/wait_patterns/public');
    setStatus(data || null);
    return data;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        await loadStatus();
      } catch (err) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not load public heatmap status',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [loadStatus]);

  const runRebuild = async () => {
    setRebuilding(true);
    try {
      const { data } = await api.post('/wait_patterns/public/rebuild');
      setStatus(data?.status || null);
      if (data?.status?.running) {
        await pollUntilIdle();
      } else {
        await loadStatus();
      }
      toast.current?.show({
        severity: 'success',
        summary: 'Heatmap rebuilt',
        detail: 'Entry pages will pick this up on the next ISR (~10 min).',
      });
    } catch (err) {
      const message =
        err?.response?.data?.error || err?.message || 'Could not rebuild the public heatmap';
      toast.current?.show({
        severity: err?.response?.status === 409 ? 'warn' : 'error',
        summary: err?.response?.status === 409 ? 'Already running' : 'Error',
        detail: message,
      });
      try {
        await loadStatus();
      } catch {
        /* keep previous status */
      }
    } finally {
      setRebuilding(false);
    }
  };

  const pollUntilIdle = async () => {
    for (let i = 0; i < 90; i += 1) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      const next = await loadStatus();
      if (!next?.running) return;
    }
    throw new Error('Rebuild is still running. Refresh this page in a minute.');
  };

  const confirmRebuild = () => {
    confirmDialog({
      header: 'Regenerate public heatmap',
      message:
        'This rebuilds the 2-week hourly series used on every entry page. It can take a minute if lean samples are missing. Continue?',
      icon: 'pi pi-refresh',
      acceptLabel: 'Regenerate',
      rejectLabel: 'Cancel',
      accept: () => {
        void runRebuild();
      },
    });
  };

  const running = Boolean(status?.running || rebuilding);

  return (
    <div className="wph-page">
      <Toast ref={toast} />
      <ConfirmDialog />

      <header className="wph-header">
        <p className="wph-kicker">Public site</p>
        <h2 className="wph-title">Entry-page heatmap</h2>
        <p className="wph-lead">
          Rebuilds the 2-week hourly series shown as the weekly pattern on every city entry page.
          Public pages pick it up on the next ISR, about 10 minutes.
        </p>
      </header>

      <section className="wph-card">
        <div className="wph-card-head">
          <div>
            <h3>Public series</h3>
            <p>Last 2 weeks, holidays stored so the public page can exclude them by default.</p>
          </div>
          <Button
            label={running ? 'Rebuilding…' : 'Regenerate heatmap'}
            icon={running ? 'pi pi-spin pi-spinner' : 'pi pi-refresh'}
            disabled={loading || running}
            onClick={confirmRebuild}
          />
        </div>

        {loading ? (
          <p className="wph-muted">Loading status…</p>
        ) : (
          <dl className="wph-stats">
            <div>
              <dt>Last rebuilt</dt>
              <dd>{formatWhen(status?.updatedAt)}</dd>
            </div>
            <div>
              <dt>Port × lane series</dt>
              <dd>{status?.seriesCount ?? 0}</dd>
            </div>
            <div>
              <dt>Lean snapshots</dt>
              <dd>{status?.leanCount ?? 0}</dd>
            </div>
            <div>
              <dt>Holiday dates in window</dt>
              <dd>{status?.holidayCount ?? 0}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>{running ? 'Running' : 'Idle'}</dd>
            </div>
          </dl>
        )}
      </section>
    </div>
  );
}
